/**
 * Hook TanStack Query per il dominio Customer/CustomerSite/CustomerContact (Slice A — Forniture).
 * Ogni hook incapsula la query key, la funzione di fetch e (per le mutation)
 * l'invalidazione delle query correlate dopo il successo.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  fetchCustomers,
  fetchCustomer,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  fetchSites,
  addSite,
  removeSite,
  fetchContacts,
  addContact,
  removeContact,
  fetchCustomerDeployments,
} from './api.js';
import type {
  Customer,
  CustomerContact,
  CustomerSite,
  CreateContactInput,
  CreateCustomerInput,
  CreateSiteInput,
  Deployment,
  UpdateCustomerInput,
} from './types.js';

// ---------------------------------------------------------------------------
// Chiavi query centralizzate
// ---------------------------------------------------------------------------

export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  detail: (id: number) => [...customerKeys.all, 'detail', id] as const,
  sites: (customerId: number) => [...customerKeys.all, 'sites', customerId] as const,
  contacts: (customerId: number) => [...customerKeys.all, 'contacts', customerId] as const,
  deployments: (customerId: number) => [...customerKeys.all, 'deployments', customerId] as const,
} as const;

// ---------------------------------------------------------------------------
// Clienti
// ---------------------------------------------------------------------------

/** Recupera la lista di tutti i clienti. */
export function useCustomers(): UseQueryResult<Customer[]> {
  return useQuery({
    queryKey: customerKeys.lists(),
    queryFn: fetchCustomers,
  });
}

/** Recupera un singolo cliente per ID. */
export function useCustomer(id: number): UseQueryResult<Customer> {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => fetchCustomer(id),
    enabled: id > 0,
  });
}

/**
 * Recupera tutte le consegne (deployments) di un cliente, ordinate per data desc.
 * Aggrega prodotti diversi consegnati allo stesso cliente.
 */
export function useCustomerDeployments(
  customerId: number,
  currentOnly = false,
): UseQueryResult<Deployment[]> {
  return useQuery({
    queryKey: [...customerKeys.deployments(customerId), { currentOnly }],
    queryFn: () => fetchCustomerDeployments(customerId, currentOnly),
    enabled: customerId > 0,
  });
}

/** Crea un nuovo cliente e invalida la lista. */
export function useCreateCustomer(): UseMutationResult<Customer, Error, CreateCustomerInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCustomer,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
}

/** Aggiorna parzialmente un cliente e invalida lista + dettaglio. */
export function useUpdateCustomer(
  id: number,
): UseMutationResult<Customer, Error, UpdateCustomerInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => updateCustomer(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) });
    },
  });
}

/** Soft-delete di un cliente e invalida lista + dettaglio. */
export function useDeleteCustomer(): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCustomer,
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Siti del cliente
// ---------------------------------------------------------------------------

/** Recupera i siti di un cliente. */
export function useSites(customerId: number): UseQueryResult<CustomerSite[]> {
  return useQuery({
    queryKey: customerKeys.sites(customerId),
    queryFn: () => fetchSites(customerId),
    enabled: customerId > 0,
  });
}

/** Aggiunge un sito e invalida la lista siti del cliente. */
export function useAddSite(
  customerId: number,
): UseMutationResult<CustomerSite, Error, CreateSiteInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => addSite(customerId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.sites(customerId) });
    },
  });
}

/** Rimuove un sito e invalida la lista siti del cliente. */
export function useRemoveSite(customerId: number): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (siteId) => removeSite(customerId, siteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.sites(customerId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Contatti del cliente
// ---------------------------------------------------------------------------

/** Recupera i contatti di un cliente. */
export function useContacts(customerId: number): UseQueryResult<CustomerContact[]> {
  return useQuery({
    queryKey: customerKeys.contacts(customerId),
    queryFn: () => fetchContacts(customerId),
    enabled: customerId > 0,
  });
}

/** Aggiunge un contatto e invalida la lista contatti del cliente. */
export function useAddContact(
  customerId: number,
): UseMutationResult<CustomerContact, Error, CreateContactInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => addContact(customerId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.contacts(customerId) });
    },
  });
}

/** Rimuove un contatto e invalida la lista contatti del cliente. */
export function useRemoveContact(customerId: number): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactId) => removeContact(customerId, contactId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.contacts(customerId) });
    },
  });
}
